import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Pagination,
  Checkbox,
  Typography,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CommonList, ListSelectionMode } from '../common/components';
import type { PreOrder } from '../../types/catalog';
import { usePreOrderData } from './usePreOrderData';
import { PRE_ORDER_STATUS_LABELS } from './preOrderStatus';
import { formatDateTime } from '../../utils/dateTimeUtils';

export default function PreOrderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;

  const { data, pagination, filters } = usePreOrderData({ companyId });

  if (data.isFirstLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {data.error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Передзамовлення
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.showOnlyActive}
                onChange={(e) => filters.setShowOnlyActive(e.target.checked)}
              />
            }
            label="Лише активні"
          />
        </Box>
      </Box>

      {data.loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ opacity: data.loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {data.preOrders.length === 0 && !data.loading ? (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: 'background.default',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="h5" gutterBottom>
              Передзамовлень немає
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Тут з'являться замовлення клієнтів з ваших каталогів
            </Typography>
          </Box>
        ) : (
          <>
            <CommonList<PreOrder>
              items={data.preOrders}
              toListItem={(item) => ({
                id: item.id,
                primary: `№${item.id} · ${item.clientName ?? item.clientEmail ?? 'Клієнт'}`,
                secondary: `${item.catalogName ?? 'Каталог'} · ${formatDateTime(item.createdDate)} · $${item.total.toFixed(2)}`,
                tertiary: PRE_ORDER_STATUS_LABELS[item.status],
              })}
              onItemClick={(item) => navigate(`/companies/${companyId}/preorders/${item.id}`)}
              selectionMode={ListSelectionMode.VIEW}
              showSelection={false}
              emptyMessage="Передзамовлення не знайдено"
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                {...pagination}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
